/**
 * 수업 페이스 — 60일 창을 주 단위로 읽는다.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 이 파일이 따로 있나
 * ─────────────────────────────────────────────────────────────
 * 서버가 주는 `paceNow` / `paceBefore` 는 **60일 창의 총 수업 수**입니다
 * (types/opsBriefing.ts MemberCardDto · 서버 opsPace.service.PACE_WINDOW_DAYS).
 * 그런데 회원 현황 화면은 그 값을 「주당 수업」 칸에 그대로 찍고 있었습니다 —
 * 60일에 10번 온 회원이 "주당 수업 10회"로 보였습니다. 주 1.2회인 회원입니다.
 *
 * 같은 값을 오늘의 브리핑은 이미 주 단위로 환산해서 보여줍니다
 * (서버 opsBriefing.scheduler.keyMetricOf 의 `weekly()`).
 * 그래서 두 화면이 같은 회원을 두고 다른 말을 하고 있었습니다.
 *
 * ⚠️ 환산은 여기 한 곳에서만 합니다. 화면에 `n / 8.57` 같은 식을 적지 마세요.
 *    두 곳에 적히는 순간 반올림이 갈리고, 같은 회원이 화면마다 1.2회와 1.1회가 됩니다.
 */

/**
 * 페이스 집계 창(일).
 *
 * ⚠️ 서버 `opsPace.service.ts` 의 `PACE_WINDOW_DAYS` 와 같아야 합니다.
 * 서버가 창을 바꾸면 이 값도 함께 바꿔야 하고, 그때까지는 두 화면이 다른 말을 합니다.
 */
export const PACE_WINDOW_DAYS = 60;

/** 창 하나가 몇 주인가 — 60일 = 8.57주 */
export const PACE_WINDOW_WEEKS = PACE_WINDOW_DAYS / 7;

/**
 * 60일 창의 총 수업 수 → 주당 수업 횟수.
 *
 * 소수 첫째 자리에서 반올림합니다. 서버 `weekly()` 와 **같은 식**입니다 —
 * 여기서 절사하거나 소수 둘째 자리까지 남기면 오늘의 브리핑과 숫자가 어긋납니다.
 */
export const weeklyPace = (countInWindow: number): number =>
    Math.round((countInWindow / PACE_WINDOW_WEEKS) * 10) / 10;

/** 화면에 그대로 쓸 문자열 — '1.2' (단위는 부르는 쪽이 붙인다) */
export const weeklyPaceText = (countInWindow: number): string => String(weeklyPace(countInWindow));

/**
 * 툴팁 한 줄 — 환산값 옆에 **원 수치를 반드시 병기**한다.
 *
 * 주당으로만 적으면 "우리 회원이 두 달에 10번 왔다"는 사실이 화면에서 사라집니다.
 * 원장님이 강사와 이야기할 때 쓰는 숫자는 그쪽입니다.
 */
export const paceTooltip = (paceBefore: number, paceNow: number): string =>
    `최근 ${PACE_WINDOW_DAYS}일 ${paceNow}회 (주 ${weeklyPaceText(paceNow)}회)\n` +
    `이전 ${PACE_WINDOW_DAYS}일 ${paceBefore}회 (주 ${weeklyPaceText(paceBefore)}회)`;
